<script setup lang="ts">
import { ClientReviewSchema, UiCard, UiChip, UiText, UiIcon, UiAccordion, UiDivider, UiButton } from '@shopexperts/shared';
import { z } from 'zod';
import { computed } from 'vue';
import RatingDisplay from './shared/RatingDisplay.vue';
import ExpertInfo from './shared/ExpertInfo.vue';

type ClientReview = z.infer<typeof ClientReviewSchema>;

const props = defineProps<{
    review: ClientReview;
}>();

const emit = defineEmits<{
    edit: [review: ClientReview];
}>();

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};


const statusConfig = computed(() => {
    switch (props.review.status) {
        case 'published':
            return { variant: 'success' as const, text: 'Published' };
        case 'pending':
            return { variant: 'warn' as const, text: 'Pending Review' };
        case 'unpublished':
            return { variant: 'dark' as const, text: 'Unpublished' };
        case 'draft':
            return { variant: 'default' as const, text: 'Draft' };
        default:
            return { variant: 'default' as const, text: props.review.status };
    }
});

const ratings = computed(() => [
    { label: 'Quality of Work', value: props.review.quality },
    { label: 'Communication', value: props.review.communication },
    { label: 'Turnaround Timelines', value: props.review.timelines },
    { label: 'Value for Money', value: props.review.value_for_money },
]);
</script>

<template>
    <UiCard padding="md">
        <div class="flex flex-col gap-6">
            <!-- Header: Expert info and status -->
            <div class="flex flex-col md:flex-row items-start md:justify-between gap-4">
                <ExpertInfo
                    :name="review.expert_name"
                    :profile-picture="review.expert_profile_picture"
                    :expert-type="review.expert_type"
                    :project-name="review.project_name"
                />

                <RatingDisplay label="Your Rating" :ratings="ratings" :rating="review.overall_rating"
                    mobile-label="Rating: " />
            </div>

            <!-- Review text -->
            <UiText as="p" variant="p" class="text-gray-700 leading-relaxed" :text="review.review_text" />

            <!-- Metadata and Actions -->
            <div class="flex items-center gap-4 text-sm text-gray-500">
                <div class="flex items-center gap-1">
                    <UiIcon name="calendar-color" class="w-3.5 h-3.5" />
                    <UiText as="span" variant="p-small" class="text-gray-500" :text="`Posted: ${ formatDate(review.created_at) }`" />
                </div>
                <div v-if="review.is_anonymous" class="flex items-center gap-1">
                    <UiIcon name="user-circle" class="w-4 h-4" />
                    <UiText as="span" variant="p-small" class="text-gray-500" text="Anonymous" />
                </div>
                <div class="flex-1"></div>
                <div class="flex items-center gap-2">
                    <UiButton 
                        variant="ghost" 
                        icon="pencil"
                        label="Edit"
                        @click="emit('edit', review)"
                    />
                    <UiChip :variant="statusConfig.variant" size="sm" :text="statusConfig.text" />
                </div>
            </div>

            <!-- Expert response -->
            <template v-if="review.review_response">
                <UiDivider />
                <UiAccordion>
                    <template #trigger>
                        <UiText as="span" variant="p-small" class="font-semibold text-gray-900"
                            text="Expert's Response" />
                    </template>
                    <div class="pt-3 flex flex-col gap-2">
                        <UiText as="p" variant="p" class="text-gray-700 leading-relaxed bg-gray-50 p-3 rounded"
                            :text="review.review_response.response_text" />
                        <UiText as="p" variant="caption-normal" class="text-gray-500">
                            Responded on {{ formatDate(review.review_response.created_at) }}
                        </UiText>
                    </div>
                </UiAccordion>
            </template>
        </div>
    </UiCard>
</template>

